import React, { useState, useEffect } from 'react';
import {
    Typography,
    Card,
    Row,
    Col,
    Statistic,
    Space,
    Spin,
    Divider,
    Alert,
    Button
} from 'antd';
import {
    DashboardOutlined,
    TeamOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    RocketOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import { Pie, Bar, Column } from '@ant-design/plots';
import { useNavigate } from 'react-router-dom';
import { adminGetExecutiveDashboardData, checkIsAdmin } from '../services/adminService';

const { Title, Text } = Typography;

const ExecutiveDashboard: React.FC = () => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const verifyAdmin = async () => {
            const adminStatus = await checkIsAdmin();
            setIsAdmin(adminStatus);
            if (adminStatus) {
                fetchDashboardData();
            } else {
                setLoading(false);
            }
        };
        verifyAdmin();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const dashboardData = await adminGetExecutiveDashboardData();
            setData(dashboardData);
        } catch (error) {
            console.error('Failed to fetch dashboard data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <Spin size="large" tip="Loading Executive Insights..." />
            </div>
        );
    }

    if (isAdmin === false) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <Alert
                    message="Access Denied"
                    description="You do not have administrative privileges to view the Executive Dashboard."
                    type="error"
                    showIcon
                />
                <Button type="primary" onClick={() => navigate('/')} style={{ marginTop: '20px' }}>
                    Return to Home
                </Button>
            </div>
        );
    }

    const { summary, difficultyIndex, programPerformance, statusDistribution } = data || {};

    // Pie Chart Config
    const pieConfig = {
        data: statusDistribution || [],
        angleField: 'value',
        colorField: 'type',
        radius: 0.8,
        innerRadius: 0.6,
        label: {
            text: 'value',
            style: { fontWeight: 'bold' },
        },
        legend: {
            color: { title: false, position: 'right' },
        },
    };

    // Difficulty Bar Chart Config
    const barConfig = {
        data: difficultyIndex || [],
        xField: 'module',
        yField: 'averageAttempts',
        label: {
            text: 'averageAttempts',
            position: 'right',
        },
        style: {
            maxWidth: 40,
            fill: '#faad14',
        },
        xAxis: {
            label: {
                autoRotate: false,
                formatter: (v: string) => v.length > 20 ? `${v.substring(0, 17)}...` : v
            }
        }
    };

    // Program Column Chart Config
    const columnConfig = {
        data: programPerformance || [],
        xField: 'trainingName',
        yField: 'successRate',
        label: {
            text: 'successRate',
            position: 'top',
            formatter: (v: any) => `${v}%`
        },
        style: {
            fill: '#52c41a',
        }
    };

    return (
        <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Space direction="vertical" size={0}>
                    <Space align="center" style={{ marginBottom: 8 }}>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/admin')}
                            type="text"
                        />
                        <Title level={2} style={{ margin: 0 }}>Executive Visibility Dashboard</Title>
                    </Space>
                    <Text type="secondary">High-level strategic insights across all training programs</Text>
                </Space>
                <Button icon={<RocketOutlined />} onClick={fetchDashboardData}>Refresh Data</Button>
            </div>

            {/* KPI Section */}
            <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} className="kpi-card" style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <Statistic
                            title="Total Enrollments"
                            value={summary?.totalEnrollments}
                            prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <Statistic
                            title="Active Programs"
                            value={summary?.activeTrainings}
                            prefix={<RocketOutlined style={{ color: '#fa541c' }} />}
                            valueStyle={{ color: '#fa541c' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <Statistic
                            title="Global Completion Rate"
                            value={summary?.totalEnrollments > 0 ? Math.round((summary.globalPassed / summary.totalEnrollments) * 100) : 0}
                            suffix="%"
                            prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <Statistic
                            title="In-Progress"
                            value={summary?.globalInProgress}
                            prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]}>
                {/* Status Mix */}
                <Col xs={24} lg={10}>
                    <Card title={<Space><DashboardOutlined /> Organizational Progress Mix</Space>} style={{ height: '100%', borderRadius: '12px' }}>
                        <div style={{ height: '300px' }}>
                            <Pie {...pieConfig} />
                        </div>
                        <Divider />
                        <Row gutter={16}>
                            <Col span={12}><Statistic title="Passed" value={summary?.globalPassed} valueStyle={{ fontSize: '16px' }} /></Col>
                            <Col span={12}><Statistic title="Failed" value={summary?.globalFailed} valueStyle={{ fontSize: '16px' }} /></Col>
                        </Row>
                    </Card>
                </Col>

                {/* Performance Leaderboard */}
                <Col xs={24} lg={14}>
                    <Card title={<Space><RocketOutlined /> Top Program Performance (Success Rate %)</Space>} style={{ height: '100%', borderRadius: '12px' }}>
                        <div style={{ height: '350px' }}>
                            <Column {...columnConfig} />
                        </div>
                    </Card>
                </Col>

                {/* Difficulty Index */}
                <Col span={24}>
                    <Card title={<Space><ClockCircleOutlined /> Content Difficulty Index (Average Attempts per Module)</Space>} style={{ borderRadius: '12px' }}>
                        <div style={{ height: '400px' }}>
                            <Bar {...barConfig} />
                        </div>
                        <Alert
                            style={{ marginTop: '16px' }}
                            message="Strategic Note"
                            description="Modules with average attempts > 1.5 may indicate content that learners find challenging or confusing. Consider reviewing these assessment questions."
                            type="info"
                            showIcon
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default ExecutiveDashboard;
